import { useState, useRef, useEffect, useCallback } from 'react';
import { adminFetch, ADMIN_ENDPOINTS } from '../config';
import './Admin.css';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

function uploadImage(file) {
  const formData = new FormData();
  formData.append('file', file);
  return adminFetch(ADMIN_ENDPOINTS.upload, { method: 'POST', body: formData })
    .then((r) => r.json())
    .then((data) => {
      if (data.error) throw new Error(data.error);
      let url = data.url || data.value || data.path || '';
      if (url && !url.startsWith('http') && !url.startsWith('/')) url = '/admin/' + (url.startsWith('uploads/') ? url : 'uploads/' + url);
      return url;
    });
}

function isEmptyContent(html) {
  if (!html || !html.trim()) return true;
  const stripped = html.replace(/<[^>]+>/g, '').trim();
  return stripped === '';
}

export default function AdminSectionEditor({ value = '', onChange, placeholder = 'Write content...', disabled }) {
  const [mode, setMode] = useState('wysiwyg'); // 'wysiwyg' | 'source'
  const [sourceText, setSourceText] = useState(value);
  const editableRef = useRef(null);
  const lastEmittedRef = useRef(undefined); // undefined so first sync runs
  const savedRangeRef = useRef(null);
  const isInitialMount = useRef(true);
  const pendingVisualHtmlRef = useRef(null); // when switching HTML → Visual, hold HTML until contenteditable mounts
  const [uploading, setUploading] = useState(false);

  const syncSourceToValue = useCallback(() => {
    setSourceText(value);
  }, [value]);

  useEffect(() => {
    if (mode === 'source') syncSourceToValue();
  }, [mode, syncSourceToValue]);

  // When value comes from parent (e.g. initial load or after switching from source), sync into contenteditable
  useEffect(() => {
    const el = editableRef.current;
    if (mode !== 'wysiwyg' || !el) return;
    // After switching HTML → Visual, contenteditable was unmounted so we stored HTML in a ref; apply it now
    if (pendingVisualHtmlRef.current !== null) {
      const html = pendingVisualHtmlRef.current;
      pendingVisualHtmlRef.current = null;
      el.innerHTML = html;
      lastEmittedRef.current = html;
      isInitialMount.current = false;
      return;
    }
    const fromParent = isInitialMount.current || value !== lastEmittedRef.current;
    if (fromParent) {
      el.innerHTML = value || '';
      lastEmittedRef.current = value;
      isInitialMount.current = false;
    }
  }, [mode, value]);

  const emitChange = useCallback(
    (html) => {
      const normalized = html || '';
      lastEmittedRef.current = normalized;
      onChange(normalized);
    },
    [onChange]
  );

  const handleInput = useCallback(() => {
    const el = editableRef.current;
    if (!el) return;
    emitChange(el.innerHTML);
  }, [emitChange]);

  const toggleSource = () => {
    if (mode === 'wysiwyg') {
      // Prefer the React value (what will actually be saved) so
      // switching modes never drops tags the browser might strip
      // from contentEditable's live DOM (e.g. <script>, <style>).
      setSourceText(value || '');
      setMode('source');
    } else {
      const html = sourceText || '';
      onChange(html);
      lastEmittedRef.current = html;
      // Contenteditable is unmounted in HTML mode; store HTML so the effect applies it when Visual mounts
      pendingVisualHtmlRef.current = html;
      setMode('wysiwyg');
    }
  };

  const exec = (cmd, value = null) => {
    editableRef.current?.focus();
    document.execCommand(cmd, false, value);
    handleInput();
  };

  const insertImageAtSelection = useCallback(
    (url) => {
      const el = editableRef.current;
      if (!el || !url) return;
      el.focus();
      const sel = window.getSelection();
      const range = savedRangeRef.current || document.createRange();
      try {
        sel.removeAllRanges();
        sel.addRange(range);
      } catch (_) {
        range.selectNodeContents(el);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }
      document.execCommand('insertHTML', false, '<img src="' + url.replace(/"/g, '&quot;') + '" alt="" />');
      handleInput();
    },
    [handleInput]
  );

  const handleImageUpload = useCallback(
    (file) => {
      if (!file || !ALLOWED_IMAGE_TYPES.includes(file.type) || file.size > MAX_IMAGE_SIZE) return;
      const el = editableRef.current;
      if (el) {
        const sel = window.getSelection();
        if (sel.rangeCount) savedRangeRef.current = sel.getRangeAt(0).cloneRange();
      }
      setUploading(true);
      uploadImage(file)
        .then((url) => insertImageAtSelection(url))
        .catch((err) => alert(err.message || 'Image upload failed'))
        .finally(() => setUploading(false));
    },
    [insertImageAtSelection]
  );

  const toolbar = (
    <div className="admin-section-editor-toolbar-buttons" role="toolbar">
      <button type="button" className="admin-section-editor-tb-btn" onClick={() => exec('bold')} title="Bold" aria-label="Bold">B</button>
      <button type="button" className="admin-section-editor-tb-btn" onClick={() => exec('italic')} title="Italic" aria-label="Italic"><i>I</i></button>
      <button type="button" className="admin-section-editor-tb-btn" onClick={() => exec('underline')} title="Underline" aria-label="Underline"><u>U</u></button>
      <button type="button" className="admin-section-editor-tb-btn" onClick={() => exec('strikeThrough')} title="Strikethrough" aria-label="Strikethrough"><s>S</s></button>
      <span className="admin-section-editor-tb-sep" />
      <button type="button" className="admin-section-editor-tb-btn" onClick={() => exec('insertUnorderedList')} title="Bullet list" aria-label="Bullet list">• List</button>
      <button type="button" className="admin-section-editor-tb-btn" onClick={() => exec('insertOrderedList')} title="Numbered list" aria-label="Numbered list">1. List</button>
      <button type="button" className="admin-section-editor-tb-btn" onClick={() => exec('formatBlock', 'blockquote')} title="Blockquote" aria-label="Blockquote">" Quote</button>
      <span className="admin-section-editor-tb-sep" />
      <button
        type="button"
        className="admin-section-editor-tb-btn"
        onClick={() => {
          const url = prompt('Link URL:', 'https://');
          if (url) exec('createLink', url);
        }}
        title="Insert link"
        aria-label="Insert link"
      >
        Link
      </button>
      <button
        type="button"
        className="admin-section-editor-tb-btn"
        onClick={() => {
          const input = document.createElement('input');
          input.setAttribute('type', 'file');
          input.setAttribute('accept', 'image/jpeg,image/png,image/gif,image/webp');
          input.onchange = () => {
            const file = input.files?.[0];
            if (file) handleImageUpload(file);
          };
          input.click();
        }}
        title="Insert image"
        aria-label="Insert image"
      >
        Image
      </button>
      <button type="button" className="admin-section-editor-tb-btn" onClick={() => exec('removeFormat')} title="Clear formatting" aria-label="Clear formatting">Clear</button>
    </div>
  );

  useEffect(() => {
    const el = editableRef.current;
    if (!el || mode !== 'wysiwyg') return;
    const onPaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.indexOf('image') !== -1) {
          e.preventDefault();
          handleImageUpload(item.getAsFile());
          return;
        }
      }
    };
    const onDrop = (e) => {
      const file = e.dataTransfer?.files?.[0];
      if (file && file.type.indexOf('image') !== -1) {
        e.preventDefault();
        handleImageUpload(file);
      }
    };
    const saveRange = () => {
      const sel = window.getSelection();
      if (el.contains(sel.anchorNode) && sel.rangeCount) savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    };
    el.addEventListener('paste', onPaste);
    el.addEventListener('drop', onDrop);
    el.addEventListener('mouseup', saveRange);
    el.addEventListener('keyup', saveRange);
    return () => {
      el.removeEventListener('paste', onPaste);
      el.removeEventListener('drop', onDrop);
      el.removeEventListener('mouseup', saveRange);
      el.removeEventListener('keyup', saveRange);
    };
  }, [mode, handleImageUpload]);

  if (mode === 'source') {
    return (
      <div className="admin-section-editor">
        <div className="admin-section-editor-toolbar">
          <span className="admin-section-editor-toolbar-label">HTML</span>
          <button type="button" className="btn btn-sm btn-ghost" onClick={toggleSource}>
            Switch to Visual
          </button>
        </div>
        <textarea
          className="admin-section-editor-source"
          value={sourceText}
          onChange={(e) => {
            const v = e.target.value;
            setSourceText(v);
            onChange(v);
          }}
          placeholder={placeholder}
          disabled={disabled}
          spellCheck={false}
        />
        <p className="admin-section-editor-hint">Full HTML is allowed. No tags or attributes are stripped.</p>
      </div>
    );
  }

  const empty = isEmptyContent(value);
  return (
    <div className="admin-section-editor admin-section-editor--wysiwyg">
      <div className="admin-section-editor-toolbar">
        <span className="admin-section-editor-toolbar-label">Visual</span>
        <button type="button" className="btn btn-sm btn-ghost" onClick={toggleSource}>
          Switch to HTML
        </button>
        {uploading && <span className="admin-section-editor-uploading">Uploading image…</span>}
      </div>
      <div className="admin-section-editor-visual-wrap">
        {toolbar}
        <div
          ref={editableRef}
          className={'admin-section-editor-editable' + (empty ? ' is-empty' : '')}
          contentEditable={!disabled}
          suppressContentEditableWarning
          data-placeholder={placeholder}
          onInput={handleInput}
        />
      </div>
      <p className="admin-section-editor-hint">
        Full HTML is allowed. You can paste or type any HTML; nothing is stripped. Drag and drop or paste images to upload.
      </p>
    </div>
  );
}
